// Push Notification Utility
// NOTE: VITE_API_URL already includes the "/api/v1" prefix (same as the axios
// baseURL in services/api.js), so fetch URLs below append only path segments.
import { storage } from './storage';

const apiUrl =
  import.meta.env.VITE_API_URL || "https://srv1309580.hstgr.cloud/puffin/api/v1";

class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this.subscription = null;
  }

  // Initialize push notifications
  async init() {
    if (!this.isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully');
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  // Request permission and subscribe. Always tied to the authenticated
  // caller (userId is derived server-side from the JWT), never client-supplied.
  async subscribe() {
    if (!this.registration) {
      await this.init();
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get VAPID public key
      const response = await fetch(`${apiUrl}/push/vapid-public-key`);
      const { publicKey } = await response.json();

      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server (authenticated - the server derives the
      // user id from the JWT, it never trusts a client-supplied user id)
      await fetch(`${apiUrl}/push/subscribe/me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          subscription: this.subscription
        })
      });

      console.log('Successfully subscribed to push notifications');
      return true;
    } catch (error) {
      console.error('Push subscription failed:', error);
      return false;
    }
  }

  // Test push notification. Pass a role (e.g. 'gmd') to test delivery to all
  // users in that role (resolved server-side); omit to test-push yourself.
  async testPush(role) {
    try {
      const response = await fetch(`${apiUrl}/push/test-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.getItem('token') || ''}`,
        },
        body: JSON.stringify(role ? { role } : {})
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Test push failed:', error);
      return false;
    }
  }

  // Check if user is subscribed
  async isSubscribed() {
    if (!this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  // Utility function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Create singleton instance
const pushService = new PushNotificationService();

export default pushService;
